import { useEffect, useRef, useState } from 'react';

import type { WorkflowError } from '../stores/table-store';
import {
  buildValidationAnnouncementText,
  planValidationAnnouncement,
  type TranslateFn,
} from '../components/primary-nav/validation-popover.utils';

/** Collapses a burst of error-list updates into one announcement. */
export const VALIDATION_STATUS_DEBOUNCE_MS = 200;

export const validationStatusRegionProps = {
  role: 'status' as const,
  'aria-live': 'polite' as const,
  'aria-atomic': true,
  className: 'c-nav-validation-status',
};

/**
 * The sentence the validation status region should show.
 *
 * The previous identity set is written when the sentence is applied, not when
 * the effect starts and not when the clear-before-repeat runs. A StrictMode
 * replay therefore does not treat the first paint as a change, and a failure
 * that cancels the follow-up leaves the set where it was so the next complete
 * pass still announces the delta. While `incomplete` is set the effect returns
 * before scheduling: the lists on screen are not this pass's verdict.
 * A repeat of the same sentence is cleared for one timeout first, because
 * setting the string the region already holds does not announce again.
 */
export const useValidationStatusAnnouncement = ({
  errors,
  incomplete,
  translate,
}: {
  errors: readonly WorkflowError[];
  incomplete: boolean;
  translate: TranslateFn;
}): string => {
  const [text, setText] = useState('');
  const previousIds = useRef<readonly string[] | null>(null);
  const followUp = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (incomplete) return undefined;

    const plan = planValidationAnnouncement(previousIds.current, errors);
    const next = buildValidationAnnouncementText(plan, translate);
    const timer = setTimeout(() => {
      if (next === '') {
        previousIds.current = plan.ids;
        setText('');
        return;
      }
      setText('');
      followUp.current = setTimeout(() => {
        followUp.current = null;
        previousIds.current = plan.ids;
        setText(next);
      }, 0);
    }, VALIDATION_STATUS_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      if (followUp.current !== null) {
        clearTimeout(followUp.current);
        followUp.current = null;
      }
    };
  }, [errors, incomplete, translate]);

  return text;
};
